import type { KeyboardEvent, ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { Hash } from "../atoms/Hash";
import { cn } from "../lib/cn";
import { StatusIndicator } from "../molecules/StatusIndicator";
import {
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableRow,
} from "../molecules/Table";
import type { SortDirection } from "../molecules/Table";

export interface BatchRow {
	merkleRoot: string;
	recordCount: number;
	createdAt: string;
	anchored: boolean;
	chainName?: string | null;
	chainId?: number | string | null;
	txHash?: string | null;
	blockNumber?: number | null;
	blockTimestamp?: string | null;
}

export type BatchSortColumn =
	| "merkleRoot"
	| "recordCount"
	| "createdAt"
	| "anchored";

export interface BatchTableProps {
	batches: BatchRow[];
	sortBy?: BatchSortColumn;
	sortDirection?: SortDirection;
	onSort?: (col: BatchSortColumn) => void;
	onSelectBatch?: (root: string) => void;
	className?: string;
}

function BatchTable({
	batches,
	sortBy,
	sortDirection,
	onSort,
	onSelectBatch,
	className,
}: BatchTableProps): ReactNode {
	function colSortDir(col: BatchSortColumn): SortDirection {
		return sortBy === col ? (sortDirection ?? null) : null;
	}

	function renderNetwork(batch: BatchRow): ReactNode {
		if (batch.chainName) {
			return <Badge variant="outline">{batch.chainName}</Badge>;
		}
		if (batch.chainId != null) {
			return (
				<Badge variant="outline" className="text-mutedForeground">
					{String(batch.chainId)}
				</Badge>
			);
		}
		return <span className="text-mutedForeground">—</span>;
	}

	function handleRowKeyDown(
		event: KeyboardEvent<HTMLTableRowElement>,
		root: string,
	): void {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onSelectBatch?.(root);
		}
	}

	return (
		<div className={cn("overflow-x-auto", className)}>
			<Table aria-label="Batch table">
				<TableHeader>
					<TableRow>
						<TableCell
							as="th"
							sortDirection={colSortDir("createdAt")}
							onSort={onSort ? () => onSort("createdAt") : undefined}
						>
							Created
						</TableCell>
						<TableCell
							as="th"
							sortDirection={colSortDir("recordCount")}
							onSort={onSort ? () => onSort("recordCount") : undefined}
						>
							Events
						</TableCell>
						<TableCell
							as="th"
							sortDirection={colSortDir("anchored")}
							onSort={onSort ? () => onSort("anchored") : undefined}
						>
							Status
						</TableCell>
						<TableCell as="th">Network</TableCell>
						<TableCell
							as="th"
							sortDirection={colSortDir("merkleRoot")}
							onSort={onSort ? () => onSort("merkleRoot") : undefined}
						>
							Reference
						</TableCell>
					</TableRow>
				</TableHeader>
				<TableBody>
					{batches.map((batch) => {
						const interactive = Boolean(onSelectBatch);
						return (
							<TableRow
								key={batch.merkleRoot}
								onClick={
									onSelectBatch
										? () => onSelectBatch(batch.merkleRoot)
										: undefined
								}
								onKeyDown={
									interactive
										? (e) => handleRowKeyDown(e, batch.merkleRoot)
										: undefined
								}
								role={interactive ? "button" : undefined}
								tabIndex={interactive ? 0 : undefined}
								aria-label={
									interactive
										? `Open record from ${batch.createdAt}`
										: undefined
								}
								className={cn(
									interactive &&
										"cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
								)}
							>
								<TableCell>{batch.createdAt}</TableCell>
								<TableCell>
									{batch.recordCount === 0 ? (
										<span className="text-mutedForeground text-sm">
											No events
										</span>
									) : (
										batch.recordCount
									)}
								</TableCell>
								<TableCell>
									<StatusIndicator
										status={batch.anchored ? "success" : "pending"}
										label={batch.anchored ? "Anchored" : "Pending"}
									/>
								</TableCell>
								<TableCell>{renderNetwork(batch)}</TableCell>
								<TableCell className="text-mutedForeground">
									<Hash value={batch.merkleRoot} length={4} />
								</TableCell>
							</TableRow>
						);
					})}
					{batches.length === 0 && (
						<TableRow>
							<TableCell
								colSpan={5}
								className="text-center text-mutedForeground py-8"
							>
								No batches found.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
}
BatchTable.displayName = "BatchTable";

export default BatchTable;
export { BatchTable };
